import React, { useState, useRef } from 'react';
import './Testimonials.css';

interface UseCaseData {
  icon: string;
  title: string;
  description: string;
  categories: number[];
}

const useCases: UseCaseData[] = [
  {
    icon: '🖼️',
    title: 'Image Classification',
    description:
      'Build and train CNN models for image recognition tasks. Visualize convolutional layers and understand how your model processes visual data.',
    categories: [1, 2, 3],
  },
  {
    icon: '📝',
    title: 'Natural Language Processing',
    description:
      'Create Transformer and RNN architectures for text analysis, sentiment classification, and language understanding. Monitor attention mechanisms in real-time.',
    categories: [1, 2, 4],
  },
  {
    icon: '🔢',
    title: 'Regression & Prediction',
    description:
      'Design MLP networks for numerical prediction tasks. Track training metrics and visualize how your model learns patterns from data.',
    categories: [1, 3, 5],
  },
  {
    icon: '🎯',
    title: 'Computer Vision',
    description:
      'Develop advanced vision models with UNet and CNN architectures. Monitor layer activations and understand feature extraction processes.',
    categories: [1, 2, 4],
  },
  {
    icon: '🔄',
    title: 'Sequence Modeling',
    description:
      'Build LSTM and GRU networks for time series analysis and sequential data processing. Visualize hidden states and memory cells during training.',
    categories: [1, 3, 5],
  },
  {
    icon: '🧠',
    title: 'Research & Experimentation',
    description:
      'Experiment with custom architectures and novel layer combinations. Use the visual builder to prototype ideas quickly and iterate on designs.',
    categories: [1, 2, 3],
  },
  {
    icon: '🚀',
    title: 'Production Deployment',
    description:
      'Deploy trained models via RESTful API for production use. Manage multiple model versions and run inference at scale with our API integration.',
    categories: [1, 2, 5],
  },
  {
    icon: '👥',
    title: 'Team Collaboration',
    description:
      'Share models, datasets, and training results with your team. Collaborate on projects and leverage community resources for faster development.',
    categories: [1, 4],
  },
  {
    icon: '📊',
    title: 'Model Analysis',
    description:
      'Deep dive into model behavior with real-time activation visualization. Understand how data flows through layers and identify optimization opportunities.',
    categories: [1, 2],
  },
];

const UseCase: React.FC<{ useCase: UseCaseData; category: number }> = ({
  useCase,
  category,
}) => {
  const isVisible = useCase.categories.includes(category);

  return (
    <article className={`testimonial-card ${!isVisible ? 'testimonial-hidden' : ''}`}>
      <div className="testimonial-content">
        <div className="testimonial-logo" style={{ fontSize: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {useCase.icon}
        </div>
        <h3 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
          {useCase.title}
        </h3>
        <p className="testimonial-text">
          {useCase.description}
        </p>
      </div>
    </article>
  );
};

const Testimonials: React.FC = () => {
  const masonryContainer = useRef<HTMLDivElement>(null);
  const [category, setCategory] = useState<number>(1);

  return (
    <div className="testimonials-container">
      <div className="testimonials-content">
        <div className="testimonials-header">
          <h2 className="testimonials-title">
            Built for every ML use case
          </h2>
          <p className="testimonials-subtitle">
            Whether you're building image classifiers, NLP models, or experimenting with novel architectures, 
            MLblocks provides the tools you need to visualize, understand, and deploy your neural networks.
          </p>
        </div>
        <div>
          <div className="testimonials-filters">
            <button
              className={`filter-button ${category === 1 ? 'filter-active' : ''}`}
              onClick={() => setCategory(1)}
            >
              <svg width="16" height={16} viewBox="0 0 16 16" fill="currentColor">
                <path d="M.062 10.003a1 1 0 0 1 1.947.455c-.019.08.01.152.078.19l5.83 3.333c.052.03.115.03.168 0l5.83-3.333a.163.163 0 0 0 .078-.188 1 1 0 0 1 1.947-.459 2.161 2.161 0 0 1-1.032 2.384l-5.83 3.331a2.168 2.168 0 0 1-2.154 0l-5.83-3.331a2.162 2.162 0 0 1-1.032-2.382Zm7.854-7.981-5.83 3.332a.17.17 0 0 0 0 .295l5.828 3.33c.054.031.118.031.17.002l5.83-3.333a.17.17 0 0 0 0-.294L8.085 2.023a.172.172 0 0 0-.17-.001ZM9.076.285l5.83 3.332c1.458.833 1.458 2.935 0 3.768l-5.83 3.333c-.667.38-1.485.38-2.153-.001l-5.83-3.332c-1.457-.833-1.457-2.935 0-3.767L6.925.285a2.173 2.173 0 0 1 2.15 0Z" />
              </svg>
              <span>View All</span>
            </button>
            <button
              className={`filter-button ${category === 2 ? 'filter-active' : ''}`}
              onClick={() => setCategory(2)}
            >
              <svg width="16" height={16} viewBox="0 0 16 16" fill="currentColor">
                <path d="M6.5 3.5a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0ZM9 6.855A3.502 3.502 0 0 0 8 0a3.5 3.5 0 0 0-1 6.855v1.656L5.534 9.65a3.5 3.5 0 1 0 1.229 1.578L8 10.267l1.238.962a3.5 3.5 0 1 0 1.229-1.578L9 8.511V6.855Zm2.303 4.74c.005-.005.01-.01.013-.016l.012-.016a1.5 1.5 0 1 1-.025.032ZM3.5 11A1.497 1.497 0 0 1 5 12.5 1.5 1.5 0 1 1 3.5 11Z" />
              </svg>
              <span>Vision</span>
            </button>
            <button
              className={`filter-button ${category === 3 ? 'filter-active' : ''}`}
              onClick={() => setCategory(3)}
            >
              <svg width="16" height={16} viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.428 10c.665-1.815 1.98-3.604 3.44-4.802-.6-1.807-1.443-3.079-2.29-3.18-1.91-.227-2.246 2.04-.174 2.962a1 1 0 1 1-.813 1.827C-1.407 5.028-.589-.491 3.815.032c1.605.191 2.925 1.811 3.79 4.07.979-.427 1.937-.51 2.735-.092.818.429 1.143 1.123 1.294 2.148.015.1.022.149.043.32.542-.537 1.003-.797 1.693-.622.64.162.894.493 1.195 1.147l.018.04a1 1 0 0 1 1.133 1.61c-.46.47-1.12.574-1.744.398a1.661 1.661 0 0 1-.87-.592 2.127 2.127 0 0 1-.224-.349 3.225 3.225 0 0 1-.55.477c-.377.253-.8.368-1.259.267-.993-.218-1.21-.779-1.367-2.05-.027-.22-.033-.262-.046-.353-.067-.452-.144-.617-.244-.67-.225-.118-.665-.013-1.206.278.297 1.243.475 2.587.516 3.941H15a1 1 0 0 1 0 2H8.68l-.025.285c-.173 1.918-.906 3.381-2.654 3.668-1.5.246-3.013-.47-3.677-1.858-.29-.637-.39-1.35-.342-2.095H1a1 1 0 0 1 0-2h1.428Zm2.11 0h2.175a18.602 18.602 0 0 0-.284-2.577c-.205.202-.408.42-.606.654A9.596 9.596 0 0 0 4.537 10Zm2.135 2H3.942c-.032.465.03.888.194 1.25.258.538.89.836 1.54.73.546-.09.888-.772.988-1.875L6.673 12Z" />
              </svg>
              <span>NLP</span>
            </button>
            <button
              className={`filter-button ${category === 4 ? 'filter-active' : ''}`}
              onClick={() => setCategory(4)}
            >
              <svg width="16" height={16} viewBox="0 0 16 16" fill="currentColor">
                <path d="M3.757 3.758a6 6 0 0 1 8.485 8.485 5.992 5.992 0 0 1-5.301 1.664 1 1 0 1 0-.351 1.969 8 8 0 1 0-4.247-2.218 1 1 0 0 0 1.415-.001L9.12 8.294v1.827a1 1 0 1 0 2 0v-4.2a.997.997 0 0 0-1-1.042H5.879a1 1 0 1 0 0 2h1.829l-4.599 4.598a6 6 0 0 1 .648-7.719Z" />
              </svg>
              <span>Research</span>
            </button>
            <button
              className={`filter-button ${category === 5 ? 'filter-active' : ''}`}
              onClick={() => setCategory(5)}
            >
              <svg width="16" height={16} viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.95.879a3 3 0 0 0-4.243 0L1.293 9.293a1 1 0 0 0-.274.51l-1 5a1 1 0 0 0 1.177 1.177l5-1a1 1 0 0 0 .511-.273l1.16-1.16a1 1 0 0 0-1.414-1.414l-.946.946-3.232.646.646-3.232 8.2-8.2a1 1 0 0 1 1.414 0l1.172 1.172a1 1 0 0 1 0 1.414l-.55.549a1 1 0 0 0 1.415 1.414l.55-.55a3 3 0 0 0 0-4.241L13.948.879ZM3.25 4.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Zm11.474 6.029-1.521-.752-.752-1.521c-.168-.341-.73-.341-.896 0l-.752 1.52-1.521.753a.498.498 0 0 0 0 .896l1.52.752.753 1.52a.5.5 0 0 0 .896 0l.752-1.52 1.52-.752a.498.498 0 0 0 0-.896Z" />
              </svg>
              <span>Production</span>
            </button>
          </div>

          <div className="testimonials-grid" ref={masonryContainer}>
            {useCases.map((useCase, index) => (
              <div key={index} className="testimonial-group">
                <UseCase useCase={useCase} category={category} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Testimonials;

